import * as i0 from '@angular/core';
import { Directive, Input, NgModule } from '@angular/core';
import { prefixScaleImageUrl } from '@sitemule/ng-components/utils/prefixScaleImageUrl';

const calculateOptimalWidth = (width, widthRounding, maxWidth) => {
    let optimalWidth = width;
    if (widthRounding) {
        optimalWidth = Math.ceil(width / widthRounding) * widthRounding;
    }
    if (optimalWidth > maxWidth) {
        return maxWidth;
    }
    return optimalWidth;
};
/**
 * Directive to auto set width to scale-image and lazy load image
 * Usage <img [smScaleImage]="imgPath" [smWidthRounding]="100" [smMaxWidth] = "1220">
 * Use <img src="{{imgPath | scaleImage: 500}}" loading="lazy"> if you want static width
 */
class ScaleImageDirective {
    constructor(el, renderer) {
        this.el = el;
        this.renderer = renderer;
        this._imageUrl = '';
        this.smWidthRounding = 0;
        this.smMaxWidth = 1500;
        this.visible = false;
    }
    set smScaleImage(imageUrl) {
        if (this._imageUrl !== imageUrl) {
            this._imageUrl = imageUrl;
            this.appendImage();
        }
    }
    get imageUrl() {
        return this._imageUrl;
    }
    ngOnInit() {
        // Sometime IntersectionObserver is not available
        //  - IOS device with latest safari but older device may have IntersectionObserver deactivated as 'Experimental feature'
        if (typeof IntersectionObserver !== 'undefined') {
            this.observer = new IntersectionObserver(([entry]) => {
                if (entry.intersectionRatio > 0 || entry.isIntersecting) {
                    this.visible = true;
                    this.appendImage();
                    this.unsubscribeObserver();
                }
            });
            this.observer.observe(this.el.nativeElement);
        }
        else {
            this.visible = true;
            this.appendImage();
        }
    }
    appendImage() {
        if (this.visible) {
            const isImg = this.el.nativeElement.tagName.toLowerCase() === 'img';
            if (isImg) {
                // Add srcset and sizes if needed
                if (this.imageUrl) {
                    this.renderer.setAttribute(this.el.nativeElement, 'src', prefixScaleImageUrl(this.imageUrl, {
                        width: calculateOptimalWidth(this.el.nativeElement.parentElement.offsetWidth, this.smWidthRounding, this.smMaxWidth),
                        padCanvas: false
                    }));
                }
                else {
                    this.renderer.removeAttribute(this.el.nativeElement, 'src');
                }
            }
            else {
                if (this.imageUrl) {
                    this.renderer.setStyle(this.el.nativeElement, 'backgroundImage', `url('${prefixScaleImageUrl(this.imageUrl, {
                        width: calculateOptimalWidth(this.el.nativeElement.offsetWidth, this.smWidthRounding, this.smMaxWidth),
                        padCanvas: false
                    })}')`);
                }
                else {
                    this.renderer.removeStyle(this.el.nativeElement, 'backgroundImage');
                }
            }
        }
    }
    unsubscribeObserver() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = undefined;
        }
    }
    ngOnDestroy() {
        this.unsubscribeObserver();
    }
}
ScaleImageDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.6", ngImport: i0, type: ScaleImageDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
ScaleImageDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.0.6", type: ScaleImageDirective, selector: "[smScaleImage]", inputs: { smScaleImage: "smScaleImage", smWidthRounding: "smWidthRounding", smMaxWidth: "smMaxWidth" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.6", ngImport: i0, type: ScaleImageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[smScaleImage]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { smScaleImage: [{
                type: Input
            }], smWidthRounding: [{
                type: Input
            }], smMaxWidth: [{
                type: Input
            }] } });

class ScaleImageDirectiveModule {
}
ScaleImageDirectiveModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.0.6", ngImport: i0, type: ScaleImageDirectiveModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ScaleImageDirectiveModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.0.6", ngImport: i0, type: ScaleImageDirectiveModule, declarations: [ScaleImageDirective], exports: [ScaleImageDirective] });
ScaleImageDirectiveModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.0.6", ngImport: i0, type: ScaleImageDirectiveModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.0.6", ngImport: i0, type: ScaleImageDirectiveModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ScaleImageDirective
                    ],
                    imports: [],
                    exports: [ScaleImageDirective],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ScaleImageDirective, ScaleImageDirectiveModule };
